import { useEffect } from 'react';
import { buildCanonicalUrl, buildPageTitle, buildStructuredData, siteMeta } from '../seo/siteMeta';

const MANAGED_META_ATTR = 'data-seo-managed';
const MANAGED_SCHEMA_ATTR = 'data-seo-jsonld';

// Lets Google and AI answer engines show full snippets and large image previews.
const INDEXABLE_ROBOTS = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';

const upsertMeta = (attribute, key, content) => {
  if (!content) return;

  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', String(content));
  tag.setAttribute(MANAGED_META_ATTR, 'true');
};

const upsertMetaByName = (name, content) => upsertMeta('name', name, content);
const upsertMetaByProperty = (property, content) => upsertMeta('property', property, content);

const upsertCanonical = (href) => {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
  tag.setAttribute(MANAGED_META_ATTR, 'true');
};

const removeCanonical = () => {
  const tag = document.head.querySelector('link[rel="canonical"]');
  if (tag) tag.remove();
};

const clearManagedStructuredData = () => {
  const previousScripts = document.head.querySelectorAll(`script[${MANAGED_SCHEMA_ATTR}="true"]`);
  previousScripts.forEach((script) => script.remove());
};

const addStructuredData = (entries = []) => {
  entries.forEach((entry) => {
    if (!entry) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(MANAGED_SCHEMA_ATTR, 'true');
    script.textContent = JSON.stringify(entry);
    document.head.appendChild(script);
  });
};

/**
 * Headless SEO manager. Pass `routeKey` (a key of `siteMeta.routeSeo`) and the
 * title, description, canonical path, share image, and JSON-LD graph all come
 * from `siteMeta`. Individual props override those values when given.
 */
const SEO = ({
  routeKey,
  title,
  description,
  path,
  image,
  type = 'website',
  structuredData,
  noindex = false
}) => {
  const route = (routeKey && siteMeta.routeSeo[routeKey]) || {};
  const resolvedTitle = title || route.title;
  const resolvedDescription = description || route.description || siteMeta.defaultDescription;
  const resolvedPath = path || route.path || '/';
  const resolvedImage = image || route.image || siteMeta.defaultImage;
  const schemaKey = noindex ? null : routeKey;

  useEffect(() => {
    const fullTitle = buildPageTitle(resolvedTitle);
    const canonicalUrl = buildCanonicalUrl(resolvedPath);
    const imageUrl = buildCanonicalUrl(resolvedImage.path);
    const schema = structuredData || (schemaKey ? buildStructuredData(schemaKey) : []);

    document.title = fullTitle;

    upsertMetaByName('description', resolvedDescription);
    upsertMetaByName('robots', noindex ? 'noindex, follow' : INDEXABLE_ROBOTS);

    upsertMetaByProperty('og:type', type);
    upsertMetaByProperty('og:site_name', siteMeta.siteName);
    upsertMetaByProperty('og:locale', siteMeta.locale);
    upsertMetaByProperty('og:title', fullTitle);
    upsertMetaByProperty('og:description', resolvedDescription);
    upsertMetaByProperty('og:url', canonicalUrl);
    upsertMetaByProperty('og:image', imageUrl);
    upsertMetaByProperty('og:image:width', resolvedImage.width);
    upsertMetaByProperty('og:image:height', resolvedImage.height);
    upsertMetaByProperty('og:image:alt', resolvedImage.alt);

    upsertMetaByName('twitter:card', 'summary_large_image');
    upsertMetaByName('twitter:site', siteMeta.twitterHandle);
    upsertMetaByName('twitter:title', fullTitle);
    upsertMetaByName('twitter:description', resolvedDescription);
    upsertMetaByName('twitter:image', imageUrl);
    upsertMetaByName('twitter:image:alt', resolvedImage.alt);

    if (noindex) {
      removeCanonical();
    } else {
      upsertCanonical(canonicalUrl);
    }

    clearManagedStructuredData();
    addStructuredData(schema);
  }, [resolvedTitle, resolvedDescription, resolvedPath, resolvedImage, type, noindex, structuredData, schemaKey]);

  return null;
};

export default SEO;
